import React, { useCallback, useEffect, useState } from 'react';
import { Btn, Loading, Msg, SubTabs, useAdminGet } from './kit';
import { type AdminTargetIn } from './adminNav';
import { type HomepageData, S } from './homepage/shared';
import HeroNumbers from './homepage/HeroNumbers';
import TrustedBy from './homepage/TrustedBy';
import SuccessStories from './homepage/SuccessStories';
import SearchChips from './homepage/SearchChips';

/**
 * /admin → Homepage (owner, 2026-10-10): the public homepage blocks an admin
 * can change without a deploy — the hero numbers and their "+X NEW!" line,
 * the "Trusted by R&D Leaders at" logo banner, the Success Stories rows, and
 * (2026-10-12) the search box's keyword chips and Surprise pool.
 * Backed by Backend/homepage_content.py (panel section "homepage"). One GET
 * feeds all four tabs; every save answers with the same shape, so the panel
 * keeps that one copy. The tabs stay mounted (hidden) while another is open,
 * so a half-done edit survives a look at the next tab.
 */

const TABS = ['Hero numbers', 'Trusted by', 'Success stories', 'Search chips'];

/** A deep link's tab=: the label itself or its snake_case ("Trusted by" → trusted_by). */
const tabKey = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
const tabFromLink = (raw: string) => TABS.find(t => t === raw || tabKey(t) === tabKey(raw));

const HomepagePanel: React.FC<{ target?: AdminTargetIn }> = ({ target }) => {
  const [tab, setTab] = useState(TABS[0]);
  useEffect(() => {
    if (!target) return;
    const t = tabFromLink(String(target.tab || '').trim());
    if (t) setTab(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target?.nonce]);

  const res = useAdminGet<HomepageData>(S, 'content');
  const [data, setData] = useState<HomepageData | null>(null);
  useEffect(() => { if (res.data?.settings) setData(res.data); }, [res.data]);
  // A save's answer is the whole page again; anything else means re-read it.
  const reload = res.reload;
  const onSaved = useCallback((d: HomepageData) => {
    if (d?.settings) setData(d); else reload();
  }, [reload]);

  // Which tabs hold unsaved edits, so switching away does not hide them.
  const [dirty, setDirty] = useState<Record<string, boolean>>({});
  const markDirty = useCallback((t: string, v: boolean) =>
    setDirty(d => (!!d[t] === v ? d : { ...d, [t]: v })), []);
  const heroDirty = useCallback((v: boolean) => markDirty(TABS[0], v), [markDirty]);
  const trustedDirty = useCallback((v: boolean) => markDirty(TABS[1], v), [markDirty]);
  const storiesDirty = useCallback((v: boolean) => markDirty(TABS[2], v), [markDirty]);
  const searchDirty = useCallback((v: boolean) => markDirty(TABS[3], v), [markDirty]);
  const elsewhere = TABS.filter(t => t !== tab && dirty[t]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <SubTabs tabs={TABS} active={tab} onChange={setTab} />
        <div className="flex items-center gap-3">
          <Btn small onClick={res.reload} disabled={res.loading}
            title="Re-read the saved settings and live counts (unsaved edits stay)">
            {res.loading && data ? 'Refreshing…' : 'Refresh'}
          </Btn>
          <a href="/" target="_blank" rel="noopener noreferrer"
            className="text-xs text-primary-700 underline">Open the homepage ↗</a>
        </div>
      </div>
      {elsewhere.length > 0 && (
        <div className="text-xs text-amber-700">
          Unsaved changes on {elsewhere.join(' and ')} — open {elsewhere.length === 1 ? 'that tab' : 'those tabs'} to
          save or discard them.
        </div>
      )}
      {res.error && (
        <Msg kind="error">
          Could not load the homepage settings: {res.error}
          {' '}<button type="button" onClick={res.reload} className="underline">Retry</button>
        </Msg>
      )}
      {!data ? (res.loading ? <Loading /> : null) : (
        <>
          <div className={tab === TABS[0] ? '' : 'hidden'}>
            <HeroNumbers data={data} onSaved={onSaved} onDirty={heroDirty} />
          </div>
          <div className={tab === TABS[1] ? '' : 'hidden'}>
            <TrustedBy data={data} onSaved={onSaved} onDirty={trustedDirty} />
          </div>
          <div className={tab === TABS[2] ? '' : 'hidden'}>
            <SuccessStories data={data} onSaved={onSaved} onDirty={storiesDirty} />
          </div>
          <div className={tab === TABS[3] ? '' : 'hidden'}>
            <SearchChips data={data} onSaved={onSaved} onDirty={searchDirty} />
          </div>
        </>
      )}
    </div>
  );
};

export default HomepagePanel;
